import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';

import React, { useReducer, useEffect } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import PropTypes from 'prop-types';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { UserProvider } from '../../hooks';
import http from '../../shared/http';
import materialTheme from '../../reactMaterialTheme';
import './EnhancedAdTable.scss';

import Mixpanel from '../../shared/Mixpanel';
import CopyExport from '../EnhancedAdRtbExport/CopyExport';
import ArchiveCreativeButton from './ArchiveCreativeButton';

const initialState = {
  creatives: [],
  filteredCreatives: [],
  showArchived: false
};

const reducer = (state, action) => {
  const getFilteredCreatives = search =>
    state.creatives.filter(creative => {
      return (
        creative.advertiser.toLowerCase().match(search) ||
        creative.name.toLowerCase().match(search)
      );
    });
  switch (action.type) {
    case 'loadCreatives':
      return {
        ...state,
        creatives: action.data,
        filteredCreatives: action.data
      };
    case 'removeCreative':
      return {
        ...state,
        creatives: state.creatives.filter(
          creative => creative.id !== action.creativeIdToRemove
        ),
        filteredCreatives: state.filteredCreatives.filter(
          creative => creative.id !== action.creativeIdToRemove
        )
      };
    case 'searchCreatives':
      return {
        ...state,
        filteredCreatives: getFilteredCreatives(action.searchTerm)
      };
    case 'toggleShowArchived':
      return {
        ...initialState,
        showArchived: !state.showArchived
      };
    case 'reset':
      return initialState;
    default:
      throw new Error('Unexpected action');
  }
};

const EnhancedAdTable = props => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { filteredCreatives, showArchived } = state;

  useEffect(() => {
    props.CurrentUserService.isAdmin().then(isAdmin => {
      if (!isAdmin) {
        props.$state.go('creativePreview.home', {}, { reload: true });
      }
    });
  }, []);

  useEffect(() => {
    http
      .get('/api/internal/partner_creative_previews', {
        params: {
          creative_action: 'banner',
          showArchived
        }
      })
      .then(({ data }) => {
        dispatch({
          type: 'loadCreatives',
          data
        });
      });

    Mixpanel.track(
      showArchived
        ? 'View archived Enhanced Ads list'
        : 'View Enhanced Ads list'
    );
  }, [showArchived]);

  const tableRowActions = creative => {
    if (showArchived) {
      return (
        <div className="enhanced-ads-table_actions-container">
          <ArchiveCreativeButton
            creativeId={creative.id}
            mode="unarchive"
            FlashService={props.FlashService}
            onClick={() => handleArchiveClick(creative.id)}
          />
        </div>
      );
    } else {
      return (
        <div className="enhanced-ads-table_actions-container">
          <button
            onClick={() => handleEditClick(creative.id)}
            className="btn enhanced-ads-table_action clickable"
          >
            Edit
          </button>
          <CopyExport
            creative={creative}
            AdMarkupService={props.AdMarkupService}
            FlashService={props.FlashService}
          />
          <ArchiveCreativeButton
            creativeId={creative.id}
            mode="archive"
            FlashService={props.FlashService}
            onClick={() => handleArchiveClick(creative.id)}
          />
          <CopyToClipboard text={shareUrl(creative)}>
            <button onClick={handleShareClick} className="btn enhanced-ads-btn">
              Share
            </button>
          </CopyToClipboard>
        </div>
      );
    }
  };

  const handleArchiveClick = creativeIdToRemove => {
    dispatch({ type: 'removeCreative', creativeIdToRemove });
  };

  const handleNameClick = id => {
    goToEditPage(id);
  };

  const handleEditClick = id => {
    goToEditPage(id);
  };

  const handleShareClick = () => {
    props.FlashService.push('success', 'Share link copied to clipboard');
    Mixpanel.track('Copy Enhanced Ads pixel from table list');
  };

  const handleSearchChange = event => {
    const search = event.target.value.toLowerCase();
    dispatch({ type: 'searchCreatives', searchTerm: search });
  };

  const shareUrl = creative => {
    return props.$state.href(
      'creativePreview.enhanced.show',
      { id: creative.id },
      { absolute: true }
    );
  };

  const goToEditPage = id => {
    Mixpanel.track('Go to Enhanced Ads edit from table list');
    props.$state.go('creativePreview.enhanced.edit', {
      id: id
    });
  };

  return (
    <MuiThemeProvider theme={materialTheme}>
      <UserProvider>
        <div className="enhanced-ads-table">
          <div
            onClick={() => dispatch({ type: 'toggleShowArchived' })}
            className="clickable enhanced-ads_archived-link"
            role="presentation"
          >
            {`Show ${showArchived ? 'All' : 'Archived'} Creatives`}
          </div>
          <div className="enhanced-ads_search-field mb2">
            <i className="ss-search enhanced-ads_search-field_icon" />
            <input
              type="text"
              className="enhanced-ads_search-field_input"
              onChange={handleSearchChange}
              placeholder="Search by brand or ad name"
            />
          </div>
          <div className="enhanced-ads_archived-label">
            {`${showArchived ? 'Archived' : 'All'} Creatives`}
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none" style={{ width: '100px' }}>
                  Ad Name
                </TableCell>
                <TableCell padding="none" style={{ width: '70px' }}>
                  Last Updated
                </TableCell>
                <TableCell style={{ width: '20px' }}>
                  Brand
                </TableCell>
                <TableCell padding="none" style={{ width: '10px' }}>
                  Impression
                </TableCell>
                <TableCell padding="none" style={{ width: '10px' }}>
                  Clicks
                </TableCell>
                <TableCell padding="none" style={{ width: '10px' }}>
                  CTR
                </TableCell>
                <TableCell style={{ width: '200px' }}>
                  Headline
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCreatives.map(creative => {
                return (
                  <TableRow
                    key={creative.id}
                    className="enhanced-ads-table_row"
                  >
                    <TableCell>
                      <div
                        onClick={() => handleNameClick(creative.id)}
                        className="clickable enhanced-ads-table_name"
                        role="presentation"
                      >
                        {creative.name}
                      </div>
                    </TableCell>
                    <TableCell padding="none">{creative.updated_at}</TableCell>
                    <TableCell>{creative.advertiser}</TableCell>
                    <TableCell padding="none">--</TableCell>
                    <TableCell padding="none">--</TableCell>
                    <TableCell padding="none">--</TableCell>
                    <TableCell>
                      <div className="enhanced-ads-table_headline-cell">
                        {creative.headline}
                        {tableRowActions(creative)}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </UserProvider>
    </MuiThemeProvider>
  );
};

EnhancedAdTable.propTypes = {
  $state: PropTypes.shape({
    params: PropTypes.object,
    go: PropTypes.func,
    href: PropTypes.func
  }).isRequired,
  AdMarkupService: PropTypes.shape({
    generate: PropTypes.func
  }).isRequired,
  CurrentUserService: PropTypes.shape({
    isAdmin: PropTypes.func
  }).isRequired,
  FlashService: PropTypes.shape({
    push: PropTypes.func
  }).isRequired
};

export default EnhancedAdTable;
